import React from 'react';
import { isValidUrl } from './url';

// Maximum content length to prevent ReDoS attacks and performance issues
const MAX_CONTENT_LENGTH = 100000; // 100KB should be more than enough for message content
// Maximum number of links to process to prevent excessive processing
const MAX_LINKS = 100;

/**
 * Validates and sanitizes a URL using the existing isValidUrl utility.
 * Returns the trimmed URL if valid, or null if invalid.
 */
const validateAndSanitizeUrl = (url: string): string | null => {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const trimmedUrl = url.trim();
  return isValidUrl(trimmedUrl) ? trimmedUrl : null;
};

/**
 * Extracts text content from HTML, removing tags but preserving text.
 * Handles nested tags by stripping all HTML.
 */
const extractTextFromHtml = (html: string): string => {
  try {
    return html.replace(/<[^>]+>/g, '').trim();
  } catch {
    // If regex fails, return empty string as fallback
    return '';
  }
};

/**
 * Trims trailing punctuation from a URL that's likely not part of the URL itself.
 * Removes common sentence-ending punctuation (.,!;:) but preserves valid URL characters
 * like query parameters (?key=value) and fragments (#section).
 *
 * Examples:
 * - "https://example.com." -> "https://example.com"
 * - "https://example.com?key=value" -> "https://example.com?key=value" (preserved)
 * - "https://example.com#section" -> "https://example.com#section" (preserved)
 * - "https://example.com," -> "https://example.com"
 */
const trimTrailingPunctuation = (url: string): string => {
  // Remove trailing sentence-ending punctuation (period, comma, exclamation, semicolon, colon)
  // But preserve ? and # which are valid URL characters (query params and fragments)
  // Also preserve closing parentheses, brackets, and slashes which are valid in URLs
  // The pattern [.,!;:]+ matches one or more of these characters at the end
  return url.replace(/[.,!;:]+$/, '').trim();
};

/**
 * Builds a validated URL match from a plain-URL regex match, or null if the
 * URL is invalid or already inside an HTML tag.
 */
const buildUrlMatch = (
  match: RegExpMatchArray,
  matchIndex: number,
  htmlMatches: Array<{ start: number; end: number }>
): { start: number; end: number; url: string } | null => {
  const isInsideHtmlTag = htmlMatches.some((htmlMatch) => matchIndex >= htmlMatch.start && matchIndex < htmlMatch.end);
  if (isInsideHtmlTag) return null;

  // Trim trailing punctuation that's likely not part of the URL (e.g., periods, commas)
  // This prevents matching "https://example.com." where the period is sentence-ending punctuation
  const trimmedUrl = trimTrailingPunctuation(match[0]);
  const url = validateAndSanitizeUrl(trimmedUrl);
  // Only add if URL is valid and uses safe protocol
  if (!url) return null;

  // Use original match boundaries for positioning (includes trailing punctuation)
  // The trimmed URL is stored, so the link will use the clean URL without punctuation
  // Any trailing punctuation will appear as regular text after the link
  return {
    start: matchIndex,
    end: matchIndex + match[0].length,
    url,
  };
};

const LINK_CLASS = {
  default: 'text-indigo-600 hover:text-indigo-700 hover:underline',
  onDark: 'text-white underline underline-offset-2 decoration-white/80 hover:decoration-white',
} as const;

type ParseMessageContentOptions = {
  linkVariant?: keyof typeof LINK_CLASS;
};

/**
 * Autolink plain `http(s)://` URLs only. Does **not** honor HTML `<a>` tags, so
 * attacker-controlled link text cannot disagree with the destination (phishing).
 * Use for untrusted user notes and for DM translations (after URL pinning).
 * Prefer `parseMessageContent` for original DM bodies that may include HTML links.
 */
export const linkifyPlainUrls = (content: string, options?: ParseMessageContentOptions): React.ReactNode[] => {
  const linkClassName = LINK_CLASS[options?.linkVariant ?? 'default'];

  if (!content) return [];
  if (typeof content !== 'string') return [String(content)];

  if (content.length > MAX_CONTENT_LENGTH) {
    const truncated = content.substring(0, MAX_CONTENT_LENGTH);
    return [
      truncated,
      <span key="truncated-warning" className="text-gray-500 italic">
        {' '}
        (Content truncated due to length)
      </span>,
    ];
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  try {
    const urlRegex = /(https?:\/\/[^\s<>"']+)/gi;
    let linkCount = 0;
    for (const match of content.matchAll(urlRegex)) {
      if (linkCount >= MAX_LINKS) break;
      const matchIndex = match.index;
      if (matchIndex === undefined) continue;

      const urlMatch = buildUrlMatch(match, matchIndex, []);
      if (!urlMatch) continue;

      if (urlMatch.start > lastIndex) {
        const textBefore = content.substring(lastIndex, urlMatch.start);
        if (textBefore) parts.push(textBefore);
      }

      parts.push(
        <a
          key={`link-${urlMatch.start}`}
          href={urlMatch.url}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClassName}
        >
          {urlMatch.url}
        </a>
      );
      lastIndex = urlMatch.end;
      linkCount++;
    }
  } catch (error) {
    console.warn('Error parsing plain URLs, returning content as text:', error);
    return [content];
  }

  if (lastIndex < content.length) {
    const remainingText = content.substring(lastIndex);
    if (remainingText) parts.push(remainingText);
  }

  return parts.length === 0 ? [content] : parts;
};

/**
 * Parses message content to convert HTML links and plain URLs into clickable React links.
 * Also handles <br> tags by converting them to newlines (whitespace-pre-wrap will handle them).
 * Includes error handling to prevent crashes from malformed input or ReDoS attacks.
 */
export const parseMessageContent = (content: string, options?: ParseMessageContentOptions): React.ReactNode[] => {
  if (!content) return [];

  const linkClassName = LINK_CLASS[options?.linkVariant ?? 'default'];

  // Input validation: check content length to prevent ReDoS attacks
  if (typeof content !== 'string') {
    return [String(content)];
  }

  if (content.length > MAX_CONTENT_LENGTH) {
    // Truncate content and show a message
    const truncated = content.substring(0, MAX_CONTENT_LENGTH);
    return [
      truncated,
      <span key="truncated-warning" className="text-gray-500 italic">
        {' '}
        (Content truncated due to length)
      </span>,
    ];
  }

  let processedContent: string;
  try {
    // Replace <br> and <br/> tags with newlines (whitespace-pre-wrap will render them)
    processedContent = content.replace(/<br\s*\/?>/gi, '\n');
  } catch (error) {
    // If regex fails, return content as-is
    console.warn('Error processing content:', error);
    return [content];
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  // First, handle HTML anchor tags
  // Matches href, then captures everything until </a> (handles nested tags)
  // Uses 's' flag (dotAll) to allow . to match newlines, and non-greedy matching
  const htmlMatches: Array<{ start: number; end: number; url: string; text: string }> = [];

  try {
    const htmlLinkRegex = /<a\s+href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gis;
    // Use matchAll() for safer iteration and better performance
    const htmlLinkMatches = Array.from(processedContent.matchAll(htmlLinkRegex));

    // Limit number of links to prevent excessive processing
    let linkCount = 0;
    for (const match of htmlLinkMatches) {
      if (linkCount >= MAX_LINKS) break;

      try {
        const url = validateAndSanitizeUrl(match[1]);
        // Only add if URL is valid and uses safe protocol
        if (url && match.index !== undefined) {
          // Extract text content, handling nested HTML tags
          const linkText = extractTextFromHtml(match[2]);
          htmlMatches.push({
            start: match.index,
            end: match.index + match[0].length,
            url,
            text: linkText || url, // Fallback to URL if no text content
          });
          linkCount++;
        }
      } catch (error) {
        // Skip invalid matches, continue processing
        console.warn('Error processing HTML link:', error);
        continue;
      }
    }
  } catch (error) {
    // If regex execution fails (e.g., ReDoS), log and continue without HTML link parsing
    console.warn('Error parsing HTML links, continuing without link parsing:', error);
  }

  // Then, handle plain URLs (but not those already in HTML tags)
  const urlMatches: Array<{ start: number; end: number; url: string }> = [];

  try {
    const urlRegex = /(https?:\/\/[^\s<>"']+)/gi;
    // Use matchAll() for safer iteration and better performance
    const urlLinkMatches = Array.from(processedContent.matchAll(urlRegex));

    // Limit number of links to prevent excessive processing
    let urlCount = 0;
    for (const match of urlLinkMatches) {
      if (urlCount >= MAX_LINKS) break;

      try {
        // Check if this URL is already inside an HTML tag
        const matchIndex = match.index;
        if (matchIndex === undefined) continue;

        const urlMatch = buildUrlMatch(match, matchIndex, htmlMatches);
        if (!urlMatch) continue;

        urlMatches.push(urlMatch);
        urlCount++;
      } catch (error) {
        // Skip invalid matches, continue processing
        console.warn('Error processing URL:', error);
        continue;
      }
    }
  } catch (error) {
    // If regex execution fails (e.g., ReDoS), log and continue without URL parsing
    console.warn('Error parsing URLs, continuing without URL parsing:', error);
  }

  try {
    // Combine and sort all matches
    const allMatches = [
      ...htmlMatches.map((m) => ({ ...m, type: 'html' as const })),
      ...urlMatches.map((m) => ({ ...m, type: 'url' as const, text: m.url })),
    ].sort((a, b) => a.start - b.start);

    // Build the parts array
    allMatches.forEach((match) => {
      try {
        // Add text before the match
        if (match.start > lastIndex) {
          const textBefore = processedContent.substring(lastIndex, match.start);
          if (textBefore) {
            parts.push(textBefore);
          }
        }

        // Add the link
        parts.push(
          <a
            key={`link-${match.start}`}
            href={match.url}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClassName}
          >
            {match.text}
          </a>
        );

        lastIndex = match.end;
      } catch (error) {
        // Skip problematic matches, continue processing
        console.warn('Error building link part:', error);
      }
    });

    // Add remaining text
    if (lastIndex < processedContent.length) {
      const remainingText = processedContent.substring(lastIndex);
      if (remainingText) {
        parts.push(remainingText);
      }
    }
  } catch (error) {
    // If building parts fails, return processed content as fallback
    console.warn('Error building message parts, returning plain content:', error);
    return [processedContent];
  }

  // If no matches, return the processed content (with <br> converted to newlines)
  if (parts.length === 0) {
    return [processedContent];
  }

  return parts;
};
